import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, CardBody, Input, Label, Table } from 'reactstrap';

import { getAdminIdToken, getApiErrorMessage } from '@/api/admin-access';
import {
  PAGE_VIEW_DEPLOYMENTS,
  PAGE_VIEW_EXCLUDED_HOSTNAMES,
} from '@/config/page-view-projects';
import { Constants } from '@/constants';

import type { PageViewResponse } from './data';
import { chartPoints, projectName } from './data';
import GrowthSkeleton from './skeleton';

const colors = [
  '#8956ff',
  '#2687ed',
  '#df9500',
  '#159d82',
  '#e45778',
  '#936339',
  '#64748b',
];
const dateInput = (offset: number) =>
  new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10);
const format = (value: number) => value.toLocaleString();

export default function GrowthDashboard() {
  const [start, setStart] = useState(() => dateInput(-6));
  const [end, setEnd] = useState(() => dateInput(0));
  const [interval, setInterval] = useState('day');
  const [hostname, setHostname] = useState('');
  const [request, setRequest] = useState({
    start: dateInput(-6),
    end: dateInput(0),
    interval: 'day',
    hostname: '',
  });
  const [data, setData] = useState<PageViewResponse | null>(null);
  const [hidden, setHidden] = useState<string[]>([]);
  const [metric, setMetric] = useState<'views' | 'sessions'>('views');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setData(null);
    setHover(null);
    (async () => {
      try {
        const token = await getAdminIdToken();
        const response = await axios.get<PageViewResponse>(
          `${Constants.apiPath}/growth/page-views`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            params: {
              ...request,
              start: `${request.start}T00:00:00.000Z`,
              end: new Date(
                Date.parse(`${request.end}T00:00:00Z`) + 86400000
              ).toISOString(),
            },
          }
        );
        if (!controller.signal.aborted) {
          setData({
            ...response.data,
            rows: response.data.rows.filter(
              (row) =>
                !PAGE_VIEW_EXCLUDED_HOSTNAMES.includes(
                  row.hostname.toLowerCase().replace(/\.$/, '')
                )
            ),
          });
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(getApiErrorMessage(err));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [request]);

  const projects = useMemo(
    () =>
      Array.from(
        new Set((data?.rows || []).map((row) => projectName(row.hostname)))
      ).sort(),
    [data]
  );
  const visible = projects.filter((name) => !hidden.includes(name));
  const rows = (data?.rows || []).filter((row) =>
    visible.includes(projectName(row.hostname))
  );
  const totals = rows
    .filter((row) => row.kind === 'total')
    .reduce(
      (sum, row) => ({
        views: sum.views + row.views,
        sessions: sum.sessions + row.sessions,
        bots: sum.bots + row.bot_views,
        sessionViews: sum.sessionViews + row.session_views,
      }),
      { views: 0, sessions: 0, bots: 0, sessionViews: 0 }
    );
  const points = useMemo(
    () => (data ? chartPoints(data, projects, metric) : []),
    [data, projects, metric]
  );
  const maxValue = Math.max(
    1,
    ...points.map((point) =>
      Math.max(
        0,
        ...point.values.filter((_, i) => !hidden.includes(projects[i]))
      )
    )
  );
  const max = Math.max(4, Math.ceil(maxValue / 4) * 4);
  const x = (i: number) => 65 + (i * 1000) / Math.max(1, points.length - 1);
  const y = (value: number) => 265 - (value / max) * 235;
  const markers = PAGE_VIEW_DEPLOYMENTS.filter(
    (marker) =>
      data &&
      Date.parse(marker.timestamp) >= Date.parse(data.start) &&
      Date.parse(marker.timestamp) < Date.parse(data.end) &&
      visible.includes(projectName(marker.hostname)) &&
      (!request.hostname ||
        marker.hostname ===
          request.hostname.trim().toLowerCase().replace(/\.$/, ''))
  );
  const routes = rows
    .filter((row) => row.kind === 'route')
    .sort((a, b) => b.views - a.views)
    .slice(0, 50);
  const activePoint = hover === null ? null : points[hover];

  return (
    <div>
      <h2>Page views by service</h2>
      <p>Compare project traffic over time. All dates and buckets use UTC.</p>
      <Card>
        <CardBody>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setRequest({ start, end, interval, hostname });
            }}
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              alignItems: 'end',
            }}
          >
            <div>
              <Label for="growth-start">From</Label>
              <Input
                id="growth-start"
                type="date"
                required
                value={start}
                max={end}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <Label for="growth-end">Through (inclusive)</Label>
              <Input
                id="growth-end"
                type="date"
                required
                value={end}
                min={start}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
            <div>
              <Label for="growth-interval">Interval</Label>
              <Input
                id="growth-interval"
                type="select"
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
              >
                <option value="day">Daily</option>
                <option value="hour">Hourly</option>
              </Input>
            </div>
            <div>
              <Label for="growth-host">Exact hostname</Label>
              <Input
                id="growth-host"
                placeholder="All hostnames"
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
              />
            </div>
            <Button type="submit" color="primary" disabled={loading}>
              {loading ? 'Loading…' : 'Apply / refresh'}
            </Button>
          </form>
          <small>Maximum range: 93 days. The current day is partial.</small>
        </CardBody>
      </Card>
      {error && (
        <Alert color="danger" role="alert">
          {error}
        </Alert>
      )}
      {loading && <GrowthSkeleton />}
      {data && (
        <>
          {!projects.length ? (
            <Alert color="info">
              No page views with a valid hostname were recorded for this range.
            </Alert>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  flexWrap: 'wrap',
                  margin: '16px 0',
                }}
              >
                {[
                  ['Page views', totals.views],
                  ['Unique sessions', totals.sessions],
                  ['Known bot views', totals.bots],
                  ['Other views', totals.views - totals.bots],
                ].map(([label, value]) => (
                  <Card key={label} style={{ flex: '1 1 180px' }}>
                    <CardBody>
                      <div>{label}</div>
                      <h3>{format(Number(value))}</h3>
                    </CardBody>
                  </Card>
                ))}
              </div>
              <Card>
                <CardBody>
                  <Label for="growth-metric">Graph measure</Label>
                  <Input
                    id="growth-metric"
                    style={{ maxWidth: 220 }}
                    type="select"
                    value={metric}
                    onChange={(e) =>
                      setMetric(e.target.value as 'views' | 'sessions')
                    }
                  >
                    <option value="views">Page views</option>
                    <option value="sessions">Unique sessions</option>
                  </Input>
                  <div
                    style={{
                      display: 'flex',
                      gap: 18,
                      flexWrap: 'wrap',
                      margin: '16px 0',
                    }}
                  >
                    {projects.map((name, i) => (
                      <label
                        key={name}
                        style={{
                          color: colors[i % colors.length],
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!hidden.includes(name)}
                          onChange={() =>
                            setHidden((current) =>
                              current.includes(name)
                                ? current.filter((item) => item !== name)
                                : [...current, name]
                            )
                          }
                        />{' '}
                        {name}
                      </label>
                    ))}
                  </div>
                  {!visible.length ? (
                    <p>Select a project to show its series.</p>
                  ) : (
                    <>
                      <svg
                        viewBox="0 0 1100 315"
                        role="img"
                        aria-label={`${metric === 'views' ? 'Page views' : 'Unique sessions'} by service, ${data.interval === 'hour' ? 'hourly' : 'daily'} UTC`}
                        style={{ width: '100%', minHeight: 240 }}
                        onMouseLeave={() => setHover(null)}
                        onMouseMove={(event) => {
                          const rect =
                            event.currentTarget.getBoundingClientRect();
                          setHover(
                            Math.max(
                              0,
                              Math.min(
                                points.length - 1,
                                Math.round(
                                  ((((event.clientX - rect.left) / rect.width) *
                                    1100 -
                                    65) /
                                    1000) *
                                    (points.length - 1)
                                )
                              )
                            )
                          );
                        }}
                      >
                        {[0, 1, 2, 3, 4].map((tick) => (
                          <g key={tick}>
                            <line
                              x1="65"
                              x2="1065"
                              y1={y((max * tick) / 4)}
                              y2={y((max * tick) / 4)}
                              stroke="currentColor"
                              opacity="0.12"
                            />
                            <text
                              x="55"
                              y={y((max * tick) / 4) + 4}
                              textAnchor="end"
                              fill="currentColor"
                              fontSize="12"
                            >
                              {format(Math.ceil((max * tick) / 4))}
                            </text>
                          </g>
                        ))}
                        {projects.map(
                          (name, i) =>
                            !hidden.includes(name) && (
                              <g key={name}>
                                <polyline
                                  fill="none"
                                  stroke={colors[i % colors.length]}
                                  strokeWidth="2"
                                  points={points
                                    .map(
                                      (point, index) =>
                                        `${x(index)},${y(point.values[i])}`
                                    )
                                    .join(' ')}
                                />
                                {points.length === 1 && (
                                  <circle
                                    cx={x(0)}
                                    cy={y(points[0].values[i])}
                                    r="4"
                                    fill={colors[i % colors.length]}
                                  />
                                )}
                              </g>
                            )
                        )}
                        {points
                          .filter(
                            (_, i) =>
                              i % Math.max(1, Math.ceil(points.length / 8)) ===
                              0
                          )
                          .map((point) => (
                            <text
                              key={point.time}
                              x={x(points.indexOf(point))}
                              y="291"
                              fill="currentColor"
                              fontSize="11"
                              textAnchor="middle"
                            >
                              {new Date(point.time)
                                .toISOString()
                                .slice(
                                  data.interval === 'hour' ? 5 : 0,
                                  data.interval === 'hour' ? 16 : 10
                                )
                                .replace('T', ' ')}
                            </text>
                          ))}
                        {markers.map((marker, i) => {
                          const position =
                            65 +
                            ((Date.parse(marker.timestamp) - points[0].time) /
                              Math.max(
                                1,
                                points[points.length - 1].time - points[0].time
                              )) *
                              1000;
                          return (
                            <line
                              key={i}
                              x1={Math.min(1065, position)}
                              x2={Math.min(1065, position)}
                              y1="20"
                              y2="265"
                              stroke="currentColor"
                              strokeDasharray="4 4"
                            >
                              <title>
                                {marker.label} · {marker.timestamp}
                              </title>
                            </line>
                          );
                        })}
                        {hover !== null && (
                          <line
                            x1={x(hover)}
                            x2={x(hover)}
                            y1="20"
                            y2="265"
                            stroke="currentColor"
                            opacity="0.35"
                          />
                        )}
                      </svg>
                      <div style={{ minHeight: 28 }} aria-live="polite">
                        {activePoint
                          ? `${new Date(activePoint.time).toISOString().replace('T', ' ').slice(0, 16)} UTC — ${projects.flatMap((name, i) => (hidden.includes(name) ? [] : [`${name}: ${format(activePoint.values[i])}`])).join(' · ')}`
                          : 'Hover over the graph for exact counts.'}
                      </div>
                    </>
                  )}
                  <p>
                    <small>
                      Sessions are unique per hostname and visitor within each
                      bucket; summary counts cover the whole range.{' '}
                      {format(totals.views - totals.sessionViews)} views lack a
                      usable session identity. Known bots are identified by user
                      agent; other traffic can include disguised bots.
                    </small>
                  </p>
                  {markers.length > 0 && (
                    <ul>
                      {markers.map((marker, i) => (
                        <li key={i}>
                          {marker.timestamp} — {marker.label} ({marker.hostname}
                          )
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
              <Card style={{ marginTop: 16 }}>
                <CardBody>
                  <h4>Top routes</h4>
                  <p>
                    Query strings and fragments removed. Up to 50 routes shown
                    for selected projects.
                  </p>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Hostname</th>
                        <th>Route</th>
                        <th>Page name</th>
                        <th>Views</th>
                        <th>Sessions</th>
                        <th>Known bots</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routes.map((row) => (
                        <tr key={`${row.hostname}:${row.route}`}>
                          <td>{projectName(row.hostname)}</td>
                          <td>{row.hostname}</td>
                          <td style={{ overflowWrap: 'anywhere' }}>
                            {row.route}
                          </td>
                          <td style={{ overflowWrap: 'anywhere' }}>
                            {row.page_name?.trim() || '—'}
                          </td>
                          <td>{format(row.views)}</td>
                          <td>{format(row.sessions)}</td>
                          <td>{format(row.bot_views)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </>
          )}
        </>
      )}
      <Card style={{ marginTop: 16 }}>
        <CardBody>
          <h4>Compare with Vercel usage</h4>
          <p>
            Client-side page tracking can miss scrapers that never execute
            JavaScript. Compare this same UTC range with function invocations,
            GB-hours, and image transformations in Vercel. These metrics are not
            collected by this dashboard.
          </p>
          <a
            href="https://vercel.com/dashboard"
            target="_blank"
            rel="noreferrer"
          >
            Open Vercel dashboard
          </a>
        </CardBody>
      </Card>
    </div>
  );
}
